import * as types from '../constants/ActionTypes'
import buildUrl from '../utils/apiHelpers'
import { getFetchWrapper, postFetchWrapper } from '../utils/fetchHelpers'

// When an admin is viewing the dashboard as a specific seller, the seller id is
// stored in the redux state and appended to the GET listing requests as `id`
// (the convention used by the other seller dashboard pages, see
// AuthenticationSupport#find_seller). It is deliberately not added to the
// member-action requests - admins only view a seller's listings, they don't act
// on their behalf.
const appendSellerId = (url, getState) => {
  const { sellerId } = getState().listingsReducer
  if (!sellerId) return url
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}id=${sellerId}`
}

export const setSellerId = (sellerId) => {
  return {
    type: types.SET_SELLER_ID,
    sellerId
  }
}

export const requestListings = () => {
  return {
    type: types.REQUEST_SELLER_LISTINGS
  }
}

export const receiveListings = (json) => {
  return {
    type: types.RECEIVE_SELLER_LISTINGS,
    listings: json.listings,
    nbOfListings: json.nbOfListings,
    nbOfListingsCapped: json.nbOfListingsCapped,
    hasMore: json.hasMore,
    page: json.page,
    success: json.success,
    receivedAt: Date.now()
  }
}

export const boostListing = (json) => {
  return {
    type: types.BOOST_LISTING,
    success: json.success,
    listing: json.updated,
    nbOfListings: json.nbOfListings
  }
}

export const markListingAsSold = (json) => {
  return {
    type: types.MARK_LISTING_AS_SOLD,
    success: json.success,
    listing: json.updated,
    nbOfListings: json.nbOfListings
  }
}

export const unmarkListingAsSold = (json) => {
  return {
    type: types.UNMARK_LISTING_AS_SOLD,
    success: json.success,
    listing: json.updated,
    nbOfListings: json.nbOfListings
  }
}

export const reserveListing = (json) => {
  return {
    type: types.RESERVE_LISTING,
    success: json.success,
    listing: json.updated,
    nbOfListings: json.nbOfListings
  }
}

export const unreserveListing = (json) => {
  return {
    type: types.UNRESERVE_LISTING,
    success: json.success,
    listing: json.updated,
    nbOfListings: json.nbOfListings
  }
}

export const relistListing = (json) => {
  return {
    type: types.RELIST_LISTING,
    listing: json.updated,
    success: json.success
  }
}

export const cancelListing = (json) => {
  return {
    type: types.CANCEL_LISTING,
    success: json.success,
    id: json.id,
    nbOfListings: json.nbOfListings
  }
}

export const checkRelistedListing = (json) => {
  return {
    type: types.CHECK_RELISTED_LISTING,
    relisted: json.relisted,
    listings: json.listings,
    nbOfListings: json.nbOfListings,
    nbOfListingsCapped: json.nbOfListingsCapped,
    hasMore: json.hasMore
  }
}

export const unmarkListingAsStale = (json) => {
  return {
    type: types.UNMARK_LISTING_AS_STALE,
    success: json.success,
    listing: json.updated,
    nbOfListings: json.nbOfListings
  }
}

export const setErrorState = (error, responseJSON = { error: `Error with status: ${error}` }) => {
  return {
    type: types.SET_ERROR_STATE,
    error: responseJSON.error
  }
}

function fetchListings (page, perPage, path, searchQuery) {
  return (dispatch, getState) => {
    dispatch(requestListings())
    return getFetchWrapper(buildUrl(appendSellerId(`/api/v1/dashboard/${path}?page=${page}&per_page=${perPage}&search=${searchQuery}`, getState)))
      .then((response) => response.json())
      .then((data) => {
        dispatch(receiveListings(data))
      })
      .catch((error) => {
        console.log('listings not received / exception', error)
        dispatch(setErrorState(error))
      })
  }
}

export const fetchAllListings = (page, perPage, searchQuery = '') => {
  return fetchListings(page, perPage, 'listings', searchQuery)
}

export const fetchPublishedListings = (page, perPage, searchQuery = '') => {
  return fetchListings(page, perPage, 'published_listings', searchQuery)
}

export const fetchSoldListings = (page, perPage, searchQuery = '') => {
  return fetchListings(page, perPage, 'sold_listings', searchQuery)
}

export const fetchOnHoldListings = (page, perPage, searchQuery = '') => {
  return fetchListings(page, perPage, 'on_hold_listings', searchQuery)
}

export const fetchAutoSoldListings = (page, perPage, searchQuery = '') => {
  return fetchListings(page, perPage, 'auto_sold_listings', searchQuery)
}

export const fetchActionRequiredListings = (page, perPage, searchQuery = '') => {
  return fetchListings(page, perPage, 'action_required_listings', searchQuery)
}

export const fetchDisapprovedListings = (page, perPage, searchQuery = '') => {
  return fetchListings(page, perPage, 'disapproved_listings', searchQuery)
}

export const fetchAwaitingApprovalListings = (page, perPage, searchQuery = '') => {
  return fetchListings(page, perPage, 'awaiting_approval_listings', searchQuery)
}

export const fetchDraftListings = (page, perPage, searchQuery = '') => {
  return fetchListings(page, perPage, 'draft_listings', searchQuery)
}

export const fetchInterestFromBuyersListings = (page, perPage, searchQuery = '') => {
  return fetchListings(page, perPage, 'interest_from_buyers_listings', searchQuery)
}

export const boostListingRequest = (listingId, selectedFilter) => {
  return dispatch => {
    return postFetchWrapper(buildUrl('/api/v1/dashboard/boost_listing?'), { id: listingId, filter: selectedFilter })
      .then((response) => response.json())
      .then((data) => {
        dispatch(boostListing(data))
      })
      .catch((error) => {
        console.log('listing not boosted')
        dispatch(setErrorState(error))
      })
  }
}

export const markListingAsSoldRequest = (listingId, selectedFilter) => {
  return dispatch => {
    return postFetchWrapper(buildUrl('/api/v1/dashboard/mark_listing_as_sold?'), { id: listingId, filter: selectedFilter })
      .then((response) => response.json())
      .then((data) => {
        dispatch(markListingAsSold(data))
      })
      .catch((error) => {
        console.log('listing not marked as sold')
        dispatch(setErrorState(error))
      })
  }
}

export const unmarkListingAsSoldRequest = (listingId, selectedFilter) => {
  return dispatch => {
    return postFetchWrapper(buildUrl('/api/v1/dashboard/unmark_listing_as_sold'), { id: listingId, filter: selectedFilter })
      .then((response) => response.json())
      .then((data) => {
        dispatch(unmarkListingAsSold(data))
      })
      .catch((error) => {
        console.log('listing not unmarked as sold')
        dispatch(setErrorState(error))
      })
  }
}

export const reserveListingRequest = (listingId, selectedFilter) => {
  return dispatch => {
    return postFetchWrapper(buildUrl('/api/v1/dashboard/reserve_listing'), { id: listingId, filter: selectedFilter })
      .then((response) => response.json())
      .then((data) => {
        dispatch(reserveListing(data))
      })
      .catch((error) => {
        console.log('listing not reserved')
        dispatch(setErrorState(error))
      })
  }
}

export const unreserveListingRequest = (listingId, selectedFilter) => {
  return dispatch => {
    return postFetchWrapper(buildUrl('/api/v1/dashboard/unreserve_listing'), { id: listingId, filter: selectedFilter })
      .then((response) => response.json())
      .then((data) => {
        dispatch(unreserveListing(data))
      })
      .catch((error) => {
        console.log('listing not unreserved')
        dispatch(setErrorState(error))
      })
  }
}

export const relistListingRequest = (listingId) => {
  return dispatch => {
    return postFetchWrapper(buildUrl('/api/v1/dashboard/relist_listing'), { id: listingId })
      .then((response) => response.json())
      .then((data) => {
        dispatch(relistListing(data))
      })
      .catch((error) => {
        console.log('listing not relisted')
        dispatch(setErrorState(error))
      })
  }
}

// Returns the response data so the caller can surface the blocked-by-order
// message (HTTP 422) in the confirmation dialog. The row is only removed from
// state on success.
export const cancelListingRequest = (listingId, selectedFilter) => {
  return dispatch => {
    return postFetchWrapper(buildUrl('/api/v1/dashboard/cancel_listing'), { id: listingId, filter: selectedFilter })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) dispatch(cancelListing(data))
        return data
      })
      .catch((error) => {
        console.log('listing not deleted')
        dispatch(setErrorState(error))
        return { success: false }
      })
  }
}

export const checkRelistedListingRequest = (listingId, selectedFilter) => {
  return dispatch => {
    return postFetchWrapper(buildUrl('/api/v1/dashboard/check_relisted_listing'), { id: listingId, filter: selectedFilter })
      .then((response) => response.json())
      .then((data) => {
        dispatch(checkRelistedListing(data))
      })
      .catch((error) => {
        console.log("listing can't be checked")
        dispatch(setErrorState(error))
      })
  }
}

export const unmarkListingAsStaleRequest = (listingId, selectedFilter) => {
  return dispatch => {
    return postFetchWrapper(buildUrl('/api/v1/dashboard/unmark_listing_as_stale'), { id: listingId, filter: selectedFilter })
      .then((response) => response.json())
      .then((data) => {
        dispatch(unmarkListingAsStale(data))
      })
      .catch((error) => {
        console.log('listing not marked as available')
        dispatch(setErrorState(error))
      })
  }
}
